import type { Entity } from '../entities/entity';
import type { Worm } from '../entities/worm';
import type { Vector2 } from '../core/constants';

export type WeaponCategory = 'projectile' | 'thrown' | 'melee' | 'strike' | 'utility' | 'homing';

export interface GameWorld {
  addEntity(entity: Entity): void;
  removeEntity(id: string): void;
  getWorms(): Worm[];
  getWind(): number;
  createExplosion(x: number, y: number, radius: number, maxDamage: number): void;
  getTerrain(): { isSolid(x: number, y: number): boolean };
  destroyTerrainCircle(x: number, y: number, radius: number, type?: 'drill' | 'blowtorch'): void;
  spawnFireAt?(x: number, y: number): void;
  getNetworkManager?(): any;
}

export interface WeaponContext {
  worm: Worm;
  world: GameWorld;
  angle: number;
  power: number;
  timer: number;
  target?: Vector2;
}

export interface WeaponResult {
  success: boolean;
  entities?: Entity[];
  /** Entities spawned later, each after its own delay (e.g. Aduka's 3 shots). */
  delayedEntities?: Array<{ delayMs: number; entity: Entity }>;
  message?: string;
  bonusWeapon?: string;
  hitWorms?: Worm[];
}

export interface WeaponDefinition {
  id: string;
  name: string;
  icon: string;
  category: WeaponCategory;

  maxAmmo: number;
  maxUsesPerTurn: number;
  powerCharging: boolean;
  timerAdjustable: boolean;
  timerRange?: [number, number];
  showAimGuide: boolean;
  allowRetreat: boolean;
  /** Turn-delay cost in delay turn-order mode (defaults via getWeaponDelay). */
  delay?: number;

  fire(ctx: WeaponContext): WeaponResult;

  loadAssets?(): Promise<void>;
}

/**
 * GunBound-style per-weapon delay costs: light shots make you act again
 * sooner; heavy shots cost future turns. Used only when the mode's turn
 * order is 'delay'; classic round-robin ignores it entirely.
 */
const WEAPON_DELAY_OVERRIDES: Record<string, number> = {
  bazooka: 750,
  shotgun: 700,
  grenade: 800,
  mortar: 800,
  cluster: 900,
  molotov: 850,
  mine: 900,
  dynamite: 1100,
  homing: 1100,
  banana: 1200,
  sheep: 1200,
  airstrike: 1300,
  holyHandGrenade: 1400,
  napalmStrike: 1400,
  supersheep: 1500,
  kamikaze: 1000,
  firePunch: 700,
  baseballBat: 700,
  teleport: 650,
  girder: 600,
  rope: 600,
  bungee: 550,
  parachute: 500,
  jetpack: 800,
  drill: 700,
  blowtorch: 700,
};

export function getWeaponDelay(weapon: WeaponDefinition): number {
  if (typeof weapon.delay === 'number') return weapon.delay;
  const override = WEAPON_DELAY_OVERRIDES[weapon.id];
  if (override !== undefined) return override;
  switch (weapon.category) {
    case 'strike':
    case 'homing':
      return 1200;
    case 'thrown':
      return 850;
    case 'utility':
      return 600;
    case 'melee':
      return 700;
    default:
      return 800;
  }
}
